import { Component, ContentChildren, Input, QueryList, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { animate, style, transition, trigger } from '@angular/animations';
import { TreeHeaderTemplateDirective } from './tree-header-template.directive';
import { TreeCellTemplateDirective } from './tree-cell-template.directive';
import { TreeFooterTemplateDirective } from './tree-footer-template.directive';
import { TreeEmptyResultsTemplateDirective } from './tree-empty-results-template.directive';

export interface BaseTreeNode<T = any> {
  label: string;
  nodeKey?: string;
  children?: TreeNode<T>[];
  renderChildHeader?: boolean;
  renderChildFooter?: boolean;
  expanded?: boolean; // <-- NEW: whether this node is expanded
}

export type TreeNode<T = any> = BaseTreeNode<T> & { [k in keyof T]: T[k]; };
export type ColConfig = {
  value: string;
  key: string;
  hideHeader?: boolean;
  hideCell?: boolean;
  hideFooter?: boolean;
  span?: number;
  classes?: string;
  sortKey?: string;
};

@Component({
  selector: 'app-tree-grid',
  standalone: true,
  imports: [CommonModule, MatIconModule, TreeCellTemplateDirective, TreeFooterTemplateDirective, TreeHeaderTemplateDirective, TreeEmptyResultsTemplateDirective],
  templateUrl: './tree-grid.component.html',
  styleUrls: ['./tree-grid.component.scss'],
  animations: [
    trigger('treeNodeRemove', [
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden', transform: 'scaleY(1)' }),
        animate('220ms ease-in', style({
          height: 0,
          opacity: 0,
          minHeight: 0,
          paddingTop: 0,
          paddingBottom: 0,
          transform: 'scaleY(0.98)'
        }))
      ])
    ])
  ],
})
export class TreeGridComponent<T> {
  @Input() nodes: TreeNode<T>[] = [];
  @Input() levelColumns: { [key: number]: Map<string, string | string[] | ColConfig>} = {};
  @Input() rootLabel: string = 'Node';

  @ContentChildren(TreeCellTemplateDirective)
  cellTemplateDirs!: QueryList<TreeCellTemplateDirective>;

  @ContentChildren(TreeHeaderTemplateDirective)
  headerTemplateDirs!: QueryList<TreeHeaderTemplateDirective>;

  @ContentChildren(TreeFooterTemplateDirective)
  footerTemplateDirs!: QueryList<TreeFooterTemplateDirective>;

  @ContentChildren(TreeEmptyResultsTemplateDirective)
  emptyResultsTemplateDirs!: QueryList<TreeEmptyResultsTemplateDirective>;

  private templateMap = new Map<string, TemplateRef<unknown>>();
  private headerTemplateMap = new Map<string, TemplateRef<unknown>>();
  private footerTemplateMap = new Map<string, TemplateRef<unknown>>();
  private emptyResultsTemplate?: TemplateRef<unknown> = undefined;

  constructor() {
  };

  ngAfterContentInit(): void {
    this.templateMap.clear();
    for (const dir of this.cellTemplateDirs) {
      // console.log(dir.name, dir.template);
      this.templateMap.set(dir.name, dir.template);
    }

    this.headerTemplateMap.clear();
    for (const dir of this.headerTemplateDirs) {
      // console.log(dir.name, dir.template);
      this.headerTemplateMap.set(dir.name, dir.template);
    }

    this.footerTemplateMap.clear();
    for (const dir of this.footerTemplateDirs) {
      // console.log(dir.name, dir.template);
      this.footerTemplateMap.set(dir.name, dir.template);
    }

    this.emptyResultsTemplate = this.emptyResultsTemplateDirs.first?.template;
  }

  originalOrder = (a: any, b: any) => 0;

  trackNode = (_: number, node: TreeNode<T>) => node.nodeKey ?? node.label;

  getTitleColumnForLevel(level: number): { key: string, value: string } {
    const cols = this.getColumnsForLevel(level);
    // console.log("cols", cols, level, this.levelColumns)
    const firstKey = cols.keys().next().value;
    const firstValue = cols.get(firstKey!);
    return { key: firstKey!, value: firstValue!.value };
  }

  getColumnsForLevel(level: number): Map<string, ColConfig> {
    // console.log(this.levelColumns[level], this.columns);
    if (!this.levelColumns[level]) {
      throw new Error(`No column configuration found for level ${level}. Check 'Map' keys.`);
    }
    return [...this.levelColumns[level].keys()].map(k => {
      const v = this.levelColumns[level].get(k);
      if (typeof v === 'string' || v instanceof Array) {
        return {
          value: v,
          key: k,
        } as ColConfig;
      } else if (typeof v === 'object' && v !== null) {
        return v as ColConfig;
      } else {
        throw new Error(`Invalid column configuration for level ${level}: ${v}`);
      }
    }).reduce((acc, col) => { acc.set(col.key, col); return acc; }, new Map<string, ColConfig>());
  }

  // UPDATED: Get template, first checking for level-prefixed name (e.g., "level0-col1")
  getTemplate(name?: string, level?: number): TemplateRef<unknown> | null {
    if (!name) return null;
    const levelPrefixed = level !== undefined ? `level${level}-${name}` : name;
    // console.log(levelPrefixed, name, this.templateMap);
    return this.templateMap.get(levelPrefixed) || this.templateMap.get(name) || null;
  }

  getEmptyResultsTemplate(): TemplateRef<unknown> | null {
    return this.emptyResultsTemplate || null;
  }

  getHeaderTemplate(name?: string, level?: number): TemplateRef<unknown> | null {
    if (!name) return null;
    const levelPrefixed = level !== undefined ? `level${level}-${name}` : name;
    // console.log(levelPrefixed, name, this.templateMap);
    return this.headerTemplateMap.get(levelPrefixed) || this.headerTemplateMap.get(name) || null;
  }

  getFooterTemplate(name?: string, level?: number): TemplateRef<unknown> | null {
    if (!name) return null;
    const levelPrefixed = level !== undefined ? `level${level}-${name}` : name;
    // console.log(levelPrefixed, name, this.templateMap);
    return this.footerTemplateMap.get(levelPrefixed) || this.footerTemplateMap.get(name) || null;
  }


  toggleNode(node: TreeNode, event: MouseEvent): void {
    // Prevent row-level click handlers (if you add any later) from firing.
    event.stopPropagation();
    node.expanded = !node.expanded;
  }
}

/**
 * Simple sample tree exactly 3 levels deep.
 * All nodes start expanded by default.
 */
const DEFAULT_TREE: TreeNode<{ col2: string, col3: string, col4: string }>[] = [
  {
    label: 'Root A',
    col2: 'A-2',
    col3: 'A-3',
    col4: 'A-4',
    expanded: true,
    children: [
      {
        label: 'Child A1',
        col2: 'A1-2',
        col3: 'A1-3',
        col4: 'A1-4',
        expanded: true,
        children: [
          {
            label: 'Grandchild A1.1',
            col2: 'A1.1-2',
            col3: 'A1.1-3',
            col4: 'A1.1-4',
          },
          {
            label: 'Grandchild A1.2',
            col2: 'A1.2-2',
            col3: 'A1.2-3',
            col4: 'A1.2-4',
          },
        ],
      },
      {
        label: 'Child A2',
        col2: 'A2-2',
        col3: 'A2-3',
        col4: 'A2-4',
        expanded: true,
        children: [
          {
            label: 'Grandchild A2.1',
            col2: 'A2.1-2',
            col3: 'A2.1-3',
            col4: 'A2.1-4',
          },
        ],
      },
    ],
  },
  {
    label: 'Root B',
    col2: 'B-2',
    col3: 'B-3',
    col4: 'B-4',
    expanded: true,
    children: [
      {
        label: 'Child B1',
        col2: 'B1-2',
        col3: 'B1-3',
        col4: 'B1-4',
        expanded: true,
        children: [
          {
            label: 'Grandchild B1.1',
            col2: 'B1.1-2',
            col3: 'B1.1-3',
            col4: 'B1.1-4',
          },
        ],
      },
    ],
  },
];
